import { create } from 'zustand';

const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds

// Cookie helpers for Safari PWA fallback
const setCookie = (name, value, days = 7) => {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    console.log('[AUTH] Cookie set:', name);
  } catch (e) {
    console.error('[AUTH] Failed to set cookie:', e);
  }
};

const getCookie = (name) => {
  try {
    const value = document.cookie.split('; ').find(row => row.startsWith(name + '='));
    return value ? decodeURIComponent(value.split('=')[1]) : null;
  } catch (e) {
    console.error('[AUTH] Failed to get cookie:', e);
    return null;
  }
};

const deleteCookie = (name) => {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch (e) {
    console.error('[AUTH] Failed to delete cookie:', e);
  }
};

// Helper to sync localStorage AND cookies immediately
const saveAuthToStorage = (user, token, now) => {
  try {
    const authState = { user, token, isAuthenticated: true, lastActivity: now };
    
    // Save to localStorage
    localStorage.setItem('auth-storage', JSON.stringify({ 
      version: 1,
      state: authState
    }));
    localStorage.setItem('last-activity', now.toString());
    localStorage.setItem('token', token);
    
    // Also save to cookies as Safari PWA fallback
    setCookie('auth-token', token);
    setCookie('auth-user', JSON.stringify(user));
    setCookie('auth-activity', now.toString());
    
    console.log('[AUTH] Saved to localStorage + cookies:', { email: user?.email, role: user?.role });
    return true;
  } catch (e) {
    console.error('[AUTH] Failed to save auth storage', e);
    // Try cookies only as last resort
    try {
      setCookie('auth-token', token);
      setCookie('auth-user', JSON.stringify(user));
      setCookie('auth-activity', now.toString());
      return true;
    } catch (cookieError) {
      console.error('[AUTH] Cookie fallback also failed', cookieError);
      return false;
    }
  }
};

const useAuthStore = create((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  lastActivity: null,
  isHydrated: false,
  
  setAuth: (user, token) => {
    const now = Date.now();
    console.log('[AUTH] setAuth called:', { email: user?.email, role: user?.role });
    
    // Save to localStorage FIRST (synchronous)
    saveAuthToStorage(user, token, now);
    
    // Then update Zustand state
    set({ 
      user, 
      token, 
      isAuthenticated: true, 
      lastActivity: now,
      isHydrated: true 
    });
  },
  
  logout: () => {
    console.log('[AUTH] Logging out');
    try {
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('last-activity');
      localStorage.removeItem('token');
      // Also clear cookies
      deleteCookie('auth-token');
      deleteCookie('auth-user');
      deleteCookie('auth-activity');
    } catch (e) {
      console.error('[AUTH] Failed to remove auth storage', e);
    }
    set({ user: null, token: null, isAuthenticated: false, lastActivity: null, isHydrated: true });
  },
  
  updateUser: (userData) => set((state) => {
    const newState = { user: { ...state.user, ...userData } };
    try {
      const authData = {
        state: { 
          user: newState.user, 
          token: state.token, 
          isAuthenticated: state.isAuthenticated,
          lastActivity: state.lastActivity 
        }
      };
      localStorage.setItem('auth-storage', JSON.stringify(authData));
    } catch (e) {
      console.error('[AUTH] Failed to update auth storage', e);
    }
    return newState;
  }),
  
  // Update last activity timestamp
  updateActivity: () => {
    const now = Date.now();
    set({ lastActivity: now });
    try {
      localStorage.setItem('last-activity', now.toString());
    } catch (e) {
      console.error('[AUTH] Failed to update activity', e);
    }
  },
  
  // Check if session has expired due to inactivity
  checkSessionExpiry: () => {
    const state = get();
    if (!state.isAuthenticated) return false;
    
    try {
      const lastActivity = localStorage.getItem('last-activity');
      if (lastActivity) {
        const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
        if (timeSinceActivity > INACTIVITY_TIMEOUT) {
          console.log('[AUTH] Session expired due to inactivity');
          get().logout();
          return true;
        }
      }
    } catch (e) {
      console.error('[AUTH] Failed to check session expiry', e);
    }
    return false;
  },
  
  // Force re-hydrate from localStorage (useful after navigation)
  forceHydrate: () => {
    console.log('[AUTH] Force hydrate called');
    get().hydrate();
  },
  
  // Hydrate from localStorage and check expiry
  hydrate: () => {
    console.log('[AUTH] Hydrating from localStorage...');
    try {
      let stored = localStorage.getItem('auth-storage');
      console.log('[AUTH] Found stored auth:', !!stored);
      
      // If no localStorage, try cookies (Safari PWA fallback)
      if (!stored) {
        console.log('[AUTH] Trying cookie fallback...');
        const cookieToken = getCookie('auth-token');
        const cookieUser = getCookie('auth-user');
        
        if (cookieToken && cookieUser) {
          try {
            const user = JSON.parse(cookieUser);
            const activity = getCookie('auth-activity') || Date.now().toString();
            console.log('[AUTH] Recovered from cookies:', { email: user?.email, role: user?.role });
            
            // Restore to localStorage for consistency
            const authState = { user, token: cookieToken, isAuthenticated: true, lastActivity: parseInt(activity, 10) };
            localStorage.setItem('auth-storage', JSON.stringify({ version: 1, state: authState }));
            localStorage.setItem('token', cookieToken);
            localStorage.setItem('last-activity', activity);
            
            stored = localStorage.getItem('auth-storage');
          } catch (e) {
            console.error('[AUTH] Cookie parsing failed:', e);
          }
        }
      }
      
      if (!stored) {
        console.log('[AUTH] No stored auth, setting isHydrated=true');
        set({ isHydrated: true });
        return;
      }
      
      const parsed = JSON.parse(stored);
      console.log('[AUTH] Parsed auth data:', { hasState: !!parsed?.state, hasUser: !!parsed?.state?.user });
      
      // Validate stored data structure
      if (!parsed || !parsed.state || !parsed.state.user || !parsed.state.token) {
        console.warn('[AUTH] Invalid auth storage structure, clearing...');
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('last-activity');
        set({ isHydrated: true });
        return;
      }
      
      const { user, token, isAuthenticated, lastActivity } = parsed.state;
      console.log('[AUTH] User from storage:', { email: user?.email, role: user?.role });
      
      // Validate user has required fields including role
      if (!user.id || !user.email || !user.role) {
        console.warn('[AUTH] User data incomplete (missing role), clearing auth...');
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('last-activity');
        set({ isHydrated: true });
        return;
      }
      
      // Check if session expired
      const storedActivity = localStorage.getItem('last-activity') || lastActivity;
      if (storedActivity) {
        const timeSinceActivity = Date.now() - parseInt(storedActivity, 10);
        if (timeSinceActivity > INACTIVITY_TIMEOUT) {
          console.log('[AUTH] Session expired on hydration');
          localStorage.removeItem('auth-storage');
          localStorage.removeItem('last-activity');
          deleteCookie('auth-token');
          deleteCookie('auth-user');
          deleteCookie('auth-activity');
          set({ isHydrated: true });
          return;
        }
      }
      
      // All checks passed - restore auth state
      console.log('[AUTH] Restoring auth state for:', user.email);
      set({ 
        user, 
        token, 
        isAuthenticated: true, 
        lastActivity: parseInt(storedActivity, 10) || Date.now(),
        isHydrated: true
      });
      
      // Also restore token to direct localStorage for backward compatibility
      localStorage.setItem('token', token);
      
    } catch (e) {
      console.error('[AUTH] Failed to hydrate auth storage, clearing...', e);
      try {
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('last-activity');
      } catch (clearError) {
        // Ignore
      }
      // Reset to default state
      set({ user: null, token: null, isAuthenticated: false, lastActivity: null, isHydrated: true });
    }
  },
}));

// Hydrate on init
if (typeof window !== 'undefined') {
  console.log('[AUTH] Initial hydration on app load');
  useAuthStore.getState().hydrate();
}

export default useAuthStore;